import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Card, cn } from "@playerelite/ui";
import { formatCount } from "../analytics/analytics-utils";
import { GeoDataItem } from "../../types";
import { activeVenueCount, CountTone, countTone, tierStripClass } from "./geo-analytics-utils";

const ROW_TONE: Record<CountTone, string> = {
  none: "border-transparent text-fg-muted",
  active: "border-action/20 bg-action/5 text-fg",
  high: "border-warning/60 bg-warning/15 text-fg shadow-sm",
};

const NAME_TONE: Record<CountTone, string> = {
  none: "",
  active: "font-medium",
  high: "font-bold",
};

const BADGE_TONE: Record<CountTone, string> = {
  none: "bg-neutral-500/10 text-fg-muted",
  active: "bg-action text-action-fg shadow-sm",
  high: "bg-warning text-warning-fg shadow-sm ring-2 ring-warning/25",
};

type Props = {
  tier: GeoDataItem;
  /** The venue picked on the map card, outlined in orange in every tier. */
  selected: string | null;
};

const TierPanel = ({ tier, selected }: Props) => {
  const counts = tier.fenceUniqueCounts;
  const active = activeVenueCount(counts);

  return (
    <Card padding="none" className="flex flex-col overflow-hidden">
      <div aria-hidden className={cn("h-1", tierStripClass(tier.tierName))} />
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
        <h3 className="text-sm font-bold uppercase tracking-wide">{tier.tierName}</h3>
        <span className="whitespace-nowrap rounded-full bg-neutral-500/10 px-2 py-1 text-xs font-medium text-fg-muted">
          {active} active
        </span>
      </div>

      {counts.length === 0 ? (
        <p className="p-5 text-sm text-fg-muted">No venues in this tier.</p>
      ) : (
        <ul className="flex flex-col gap-1 p-3">
          {counts.map((c) => {
            const tone = countTone(c.distinctUserCount);
            const isSelected = c.geofenceName === selected;
            return (
              <li
                key={c.geofenceName}
                aria-current={isSelected || undefined}
                className={cn(
                  "flex min-h-9 items-center justify-between gap-3 rounded-md border px-2.5 py-1.5 transition-colors",
                  ROW_TONE[tone],
                  isSelected && "ring-2 ring-warning ring-offset-1 ring-offset-surface"
                )}
              >
                <span className={cn("text-xs leading-tight", NAME_TONE[tone])}>
                  {c.geofenceName}
                </span>
                <span
                  className={cn(
                    "inline-flex min-w-8 shrink-0 items-center justify-center gap-1 rounded-full px-2 py-1 font-mono text-xs font-semibold tabular-nums",
                    BADGE_TONE[tone]
                  )}
                >
                  <PersonOutlineIcon aria-hidden sx={{ fontSize: 12 }} />
                  {formatCount(c.distinctUserCount)}
                  <span className="sr-only"> members</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

export default TierPanel;
